/**
 * Client de signaling.
 *
 * ## Reconnexion
 *
 * Le WebSocket peut tomber pour des raisons banales : mise en veille de l'ordinateur,
 * bascule Wi-Fi/4G, redémarrage du reverse proxy. La reconnexion est donc automatique,
 * avec un délai exponentiel et une gigue.
 *
 * La gigue n'est pas cosmétique : sans elle, tous les clients d'un serveur redémarré
 * reviennent au même instant et produisent un pic de reconnexions synchronisées.
 *
 * ## Erreurs fatales
 *
 * Certaines erreurs ne se répareront jamais en réessayant : salle pleine, rôle déjà pris,
 * version de protocole incompatible. Dans ces cas, insister ne ferait qu'aggraver la
 * charge serveur et masquer la cause. La reconnexion est donc arrêtée net.
 */

import {
  PING_INTERVAL_MS,
  decodeServerMsg,
  type ClientMsg,
  type ErrorCode,
  type PeerInfo,
  type PeerState,
  type Role,
  type RoomPolicy,
  type ServerMsg,
  type SignalPayload,
} from '@attrape/shared';

const ENDPOINT = '/signaling';
const BASE_DELAY_MS = 600;
const MAX_DELAY_MS = 20_000;

/**
 * Erreurs qu'une nouvelle tentative ne peut pas résoudre.
 *
 * `room_consumed` en fait partie, et son absence était un défaut à part entière : un lien
 * à usage unique déjà servi est mort **définitivement**. Sans cette entrée, le client
 * prenait le refus pour un incident passager et retentait sans fin, toutes les 20 secondes,
 * tant que la page restait ouverte. Un lien déposé dans une conversation de groupe
 * produisait donc autant de boucles de reconnexion que de lecteurs — pour un résultat qui
 * ne pouvait jamais changer. Aucune temporisation ne rend une salle consommée à nouveau
 * joignable.
 */
const FATAL_CODES: ReadonlySet<ErrorCode> = new Set<ErrorCode>([
  'role_taken',
  'room_full',
  'unsupported_version',
  'room_consumed',
]);

export type SignalingStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'failed' | 'closed';

export interface SignalingHandlers {
  onWelcome: (message: Extract<ServerMsg, { t: 'welcome' }>) => void;
  onPeerJoin: (peer: PeerInfo) => void;
  onPeerLeft: (peerId: string) => void;
  onSignal: (from: string, payload: SignalPayload) => void;
  onPeerState: (from: string, state: PeerState) => void;
  /**
   * Trame relayée reçue du pair distant.
   *
   * Le `seq` vient de l'émetteur et sert à acquitter la trame : l'accusé est renvoyé
   * par l'appelant via `sendRelayAck`, après quoi la fenêtre de l'émetteur se libère.
   */
  onRelay: (from: string, seq: number, data: string) => void;
  /** Le pair distant a acquitté la trame de séquence `seq`. */
  onRelayAck: (seq: number) => void;
  onServerError: (code: ErrorCode, message: string) => void;
  onStatus: (status: SignalingStatus) => void;
  onLatency: (milliseconds: number | null) => void;
}

function endpointUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  // On passe par l'hôte courant : le reverse proxy route `/signaling` vers le service.
  // Aucune URL de serveur n'est codée en dur, ni en développement ni en production.
  return `${protocol}//${window.location.host}${ENDPOINT}`;
}

export class SignalingClient {
  private socket: WebSocket | null = null;
  private status: SignalingStatus = 'idle';
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pingSentAt = 0;
  private stopRequested = false;

  constructor(
    private readonly roomId: string,
    private readonly role: Role,
    private readonly handlers: SignalingHandlers,
    private readonly name?: string,
    /**
     * Politique d'usage de la salle, annoncée au serveur.
     *
     * Seul l'émetteur la choisit : c'est lui qui crée le lien, donc lui seul sait s'il
     * accepte plusieurs destinataires. Le destinataire l'annonce quand même, sans effet —
     * le serveur ignore la politique des entrants suivants.
     */
    private readonly policy: RoomPolicy = 'one-shot',
  ) {}

  get currentStatus(): SignalingStatus {
    return this.status;
  }

  connect(): void {
    this.stopRequested = false;
    this.open();
  }

  private setStatus(status: SignalingStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.handlers.onStatus(status);
  }

  private open(): void {
    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');

    let socket: WebSocket;
    try {
      socket = new WebSocket(endpointUrl());
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
      this.setStatus('open');
      this.send({
        t: 'hello',
        room: this.roomId,
        role: this.role,
        policy: this.policy,
        ...(this.name === undefined ? {} : { name: this.name }),
      });
      this.startPing();
    };

    socket.onmessage = (event) => {
      if (typeof event.data !== 'string') return;
      const decoded = decodeServerMsg(event.data);
      if (!decoded.ok) {
        // Un message serveur illisible signale une incompatibilité de version ou un
        // intermédiaire qui réécrit le trafic. On l'ignore sans casser la session.
        if (import.meta.env.DEV) console.warn('Message serveur illisible', decoded.error);
        return;
      }
      this.dispatch(decoded.value);
    };

    socket.onerror = () => {
      // L'erreur est toujours suivie d'une fermeture : c'est `onclose` qui décide.
    };

    socket.onclose = () => {
      this.stopPing();
      this.handlers.onLatency(null);
      if (this.stopRequested) {
        this.setStatus('closed');
        return;
      }
      this.scheduleReconnect();
    };
  }

  private dispatch(message: ServerMsg): void {
    switch (message.t) {
      case 'welcome':
        this.handlers.onWelcome(message);
        return;
      case 'peer-join':
        this.handlers.onPeerJoin(message.peer);
        return;
      case 'peer-left':
        this.handlers.onPeerLeft(message.peerId);
        return;
      case 'signal':
        this.handlers.onSignal(message.from, message.payload);
        return;
      case 'relay':
        this.handlers.onRelay(message.from, message.seq, message.data);
        return;
      case 'relay-ack':
        this.handlers.onRelayAck(message.seq);
        return;
      case 'peer-state':
        this.handlers.onPeerState(message.from, message.state);
        return;
      case 'pong':
        this.handlers.onLatency(Math.max(0, Date.now() - this.pingSentAt));
        return;
      case 'error': {
        this.handlers.onServerError(message.code, message.message);
        if (FATAL_CODES.has(message.code)) {
          this.stopRequested = true;
          this.setStatus('failed');
          this.close();
        }
        return;
      }
      // `relay` et `relay-ack` sont traités ci-dessus : ils portent le chemin de secours
      // quand le P2P direct est impossible. Aucun autre cas ne subsiste ici.
      default: {
        const exhaustive: never = message;
        if (import.meta.env.DEV) console.warn('Message serveur non géré', exhaustive);
      }
    }
  }

  /** Envoie un message. Renvoie `false` si la socket n'est pas ouverte. */
  send(message: ClientMsg): boolean {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    socket.send(JSON.stringify(message));
    return true;
  }

  /** Envoie une trame au relais. Renvoie `false` si la socket n'est pas ouverte. */
  sendRelay(seq: number, data: string): boolean {
    return this.send({ t: 'relay', seq, data });
  }

  /** Acquitte une trame relayée reçue. Renvoie `false` si la socket n'est pas ouverte. */
  sendRelayAck(seq: number): boolean {
    return this.send({ t: 'relay-ack', seq });
  }

  /**
   * Annonce son propre état au pair distant.
   *
   * Le destinataire s'en sert pour signaler la **fin de son usage** du lien : transfert
   * terminé (`done`) ou refus explicite (`cancelled`). Le serveur traduit ces deux états
   * en consommation d'une salle à usage unique.
   *
   * Ce message est le seul signal de consommation, et il est applicatif à dessein : un
   * état annoncé est un geste délibéré, alors qu'une socket qui tombe ne distingue pas un
   * départ d'une coupure réseau. Consommer sur la fermeture de socket rendait la reprise
   * après coupure impossible sur un lien `one-shot`.
   */
  sendPeerState(state: PeerState): boolean {
    return this.send({ t: 'peer-state', state });
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      this.pingSentAt = Date.now();
      if (!this.send({ t: 'ping', ts: this.pingSentAt })) {
        this.handlers.onLatency(null);
      }
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer === null) return;
    clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  /**
   * Programme une reconnexion avec délai exponentiel et gigue.
   *
   * La gigue (± 30 %) évite que tous les clients reviennent simultanément après un
   * redémarrage serveur.
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;

    const exponential = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** this.attempt);
    const jitter = exponential * 0.3 * (Math.random() * 2 - 1);
    const delay = Math.max(BASE_DELAY_MS, Math.round(exponential + jitter));
    this.attempt += 1;

    this.setStatus('reconnecting');
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay);
  }

  /** Signale au serveur qu'on quitte la salle, puis ferme la connexion. */
  leave(reason?: string): void {
    this.send(reason === undefined ? { t: 'bye' } : { t: 'bye', reason });
    this.close();
  }

  close(): void {
    this.stopRequested = true;
    this.stopPing();
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const socket = this.socket;
    this.socket = null;
    if (socket && socket.readyState <= WebSocket.OPEN) {
      socket.close(1000, 'client closing');
    }
    this.setStatus('closed');
  }
}
